import React from 'react'

import { routes } from '@sketch/modules-common'
import { localStorageKeys } from '@sketch/constants'

import {
  setStringifiedItem,
  getParsedItem,
  formatPriceWithCurrency,
} from '@sketch/utils'

//Components
import { Modal, ModalInjectedProps } from '@sketch/components'

import {
  SubTitle,
  Columns,
  Column,
  Paragraph,
  Note,
  StyledModal,
  StyledButton,
  StyledLinkButton,
  List,
  Item,
  Checkmark,
  Skeleton,
} from './WorkspaceWelcomeModal.styles'

import {
  useGetAvailablePlansQuery,
  useGetCustomerQuery,
} from '@sketch/gql-types'

interface WorkspaceWelcomeModalProps extends ModalInjectedProps {
  workspaceId: string
  customerId: string
}

const WorkspaceWelcomeModal: React.FCC<WorkspaceWelcomeModalProps> = ({
  hideModal,
  workspaceId,
  customerId,
}) => {
  const { data } = useGetCustomerQuery({
    variables: { customerId },
    skip: !workspaceId,
  })

  const { data: planData } = useGetAvailablePlansQuery({
    fetchPolicy: 'network-only',
  })

  const customerCurrency =
    data?.customer?.billingSummary.current.currency || 'USD'

  const monthlyPlan = planData?.availablePlans.find(
    plan => plan.type === 'MONTHLY' && plan.product === 'TEAMS'
  )

  const yearlyPlan = planData?.availablePlans.find(
    plan => plan.type === 'YEARLY' && plan.product === 'TEAMS'
  )

  const monthlyPrice = monthlyPlan?.prices.find(
    price => price.currency === customerCurrency
  )

  const yearlyPrice = yearlyPlan?.prices.find(
    price => price.currency === customerCurrency
  )

  const handleHideModal = () => {
    // Grab all dismissed workspace ID's from local storage
    const previouslyDismissedIDs =
      getParsedItem(localStorageKeys.dismissedWelcomeModal) ?? []

    const currentDismissedIDs = [...previouslyDismissedIDs, workspaceId]

    // Set the current workspace ID to the list of dismissed modals
    setStringifiedItem(
      localStorageKeys.dismissedWelcomeModal,
      currentDismissedIDs
    )
    hideModal()
  }

  return (
    <StyledModal onCancel={handleHideModal}>
      <Modal.Body>
        <Columns>
          <Column>
            <SubTitle>Try Sketch for Free</SubTitle>
            <List>
              <Item>
                <Checkmark />
                Everything unlocked for 30 days
              </Item>
              <Item>
                <Checkmark />
                Access our world-class Mac app
              </Item>
              <Item>
                <Checkmark />A Workspace for storing files
              </Item>
              <Item>
                <Checkmark />
                Powerful collaboration tools
              </Item>
            </List>
            <StyledButton
              variant="secondary-untinted"
              onClick={handleHideModal}
              size="40"
            >
              Continue to Workspace
            </StyledButton>
          </Column>
          <Column withBackground>
            <SubTitle>
              Then Just{' '}
              {yearlyPrice ? (
                formatPriceWithCurrency(
                  yearlyPrice.monthly,
                  yearlyPrice.currency
                )
              ) : (
                <Skeleton />
              )}{' '}
              per Month
            </SubTitle>
            <Paragraph>
              After your trial ends, subscribe for only{' '}
              {yearlyPrice ? (
                formatPriceWithCurrency(
                  yearlyPrice.monthly,
                  yearlyPrice.currency,
                  { trimDecimalIfZero: true }
                )
              ) : (
                <Skeleton />
              )}{' '}
              per month (billed yearly) – or{' '}
              {monthlyPrice ? (
                formatPriceWithCurrency(
                  monthlyPrice.monthly,
                  monthlyPrice.currency,
                  { trimDecimalIfZero: true }
                )
              ) : (
                <Skeleton />
              )}{' '}
              per month (billed monthly).
            </Paragraph>
            <StyledLinkButton
              variant="primary-untinted"
              to={routes.WORKSPACE_SUBSCRIBE.create({
                workspaceId,
              })}
              onClick={handleHideModal}
              size="40"
            >
              Subscribe Now
            </StyledLinkButton>
          </Column>
        </Columns>
        <Note>
          After the trial ends you will no longer have access to Workspaces or
          the Mac app. Expired Workspaces become inactive after 90 days.
        </Note>
      </Modal.Body>
    </StyledModal>
  )
}

export default WorkspaceWelcomeModal
